import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { SIGNUP_CONTENT_NAME, trackSubscribeReturn } from "../metaSignup";
import { metaPixelService } from "../services/metaPixelService";
import { hasSensitiveSearch } from "../utils/analyticsUrl";
import {
  CONSENT_EVENT,
  CONSENT_KEY,
  type CookiePrefs,
  readMarketingConsent,
} from "../utils/cookieConsent";
import { rememberFbclid } from "../utils/metaClick";

/** Loads the Meta pixel after marketing consent and records page and landing views. */
export default function MetaRouteTracker() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [marketing, setMarketing] = useState(readMarketingConsent);

  useEffect(() => {
    const apply = () => setMarketing(readMarketingConsent());

    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<CookiePrefs>).detail;
      if (detail && typeof detail.marketing === "boolean") {
        setMarketing(detail.marketing);
        return;
      }
      apply();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === CONSENT_KEY) apply();
    };

    apply();
    window.addEventListener(CONSENT_EVENT, onConsent);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onConsent);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    rememberFbclid(location.search);
  }, [location.search]);

  useEffect(() => {
    if (!marketing) {
      metaPixelService.disable();
      return;
    }

    metaPixelService.enable();
    metaPixelService.setUser(currentUser?.email ?? null, currentUser?.uid ?? null);
    if (hasSensitiveSearch(location.search)) return;

    metaPixelService.track("PageView");
    if (location.pathname === "/") {
      metaPixelService.track("ViewContent", {
        content_name: SIGNUP_CONTENT_NAME,
        content_type: "product",
      });
    }
    if (new URLSearchParams(location.search).get("subscription_success") === "true") {
      trackSubscribeReturn();
    }
  }, [marketing, location.pathname, location.search, currentUser?.email, currentUser?.uid]);

  return null;
}
